/**
 * URL Slug Utilities
 *
 * Handles slug validation, reserved keywords, and slug generation
 * for the URL architecture where location slugs are at the root level.
 */

/**
 * Reserved slugs that cannot be used as location identifiers.
 * These are org-wide routes that take precedence over location routing.
 */
export const RESERVED_SLUGS = [
  // Core resources (org-wide)
  'dashboard',
  'clients',
  'aircraft',
  'aircrafts',
  'leases',
  'contacts',
  'locations',

  // Settings & admin
  'settings',
  'admin',
  'reports',

  // Financial
  'invoices',
  'payments',

  // Auth
  'login',
  'signup',
  'logout',
  'forgot-password',

  // System
  'api',
  'auth',
  'help',
  'docs',
  'status',
  'health',

  // Actions (reserved for future use)
  'new',
  'edit',
  'delete',
  'search',

  // Special
  'drag-test', // Dev route
] as const

export type ReservedSlug = (typeof RESERVED_SLUGS)[number]

/**
 * Check if a slug is a reserved keyword
 */
export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.includes(slug.toLowerCase() as ReservedSlug)
}

/**
 * Convert a location_code to a URL-safe slug
 * - Lowercase
 * - Replace hyphens with underscores (optional, for URL readability)
 *
 * @example
 * toLocationSlug('KDET') // 'kdet'
 * toLocationSlug('PHX-MAIN') // 'phx-main'
 */
export function toLocationSlug(locationCode: string): string {
  return locationCode.toLowerCase()
}

/**
 * Normalize a slug for comparison (case-insensitive)
 */
export function normalizeSlug(slug: string): string {
  return slug.toLowerCase().trim()
}

/**
 * Validate a location slug
 * Returns true if valid, false if reserved or invalid format
 */
export function isValidLocationSlug(slug: string): boolean {
  if (!slug || slug.length < 2 || slug.length > 50) {
    return false
  }

  // Must not be a reserved keyword
  if (isReservedSlug(slug)) {
    return false
  }

  // Must be alphanumeric with hyphens/underscores, starting with a letter
  const validPattern = /^[a-z][a-z0-9_-]*$/i
  return validPattern.test(slug)
}

/**
 * Generate a short ID from a UUID (8 characters, URL-safe base64)
 * Used for entity references that may be renamed (clients, leases)
 */
export function generateShortId(uuid: string): string {
  // Remove hyphens from UUID
  const hex = uuid.replace(/-/g, '')

  // Convert hex to bytes
  const bytes = new Uint8Array(hex.match(/.{2}/g)!.map((byte) => parseInt(byte, 16)))

  // Convert to base64url
  const base64 = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')

  // Take first 8 characters
  return base64.slice(0, 8)
}

/**
 * Extract short ID from an entity reference slug
 * @example
 * extractShortId('abc-aviation-VQ6EAOK') // 'VQ6EAOK'
 * extractShortId('lse-2024-015-x7Kj9mQ') // 'x7Kj9mQ'
 */
export function extractShortId(slug: string): string | null {
  const lastHyphen = slug.lastIndexOf('-')
  if (lastHyphen === -1 || lastHyphen === slug.length - 1) {
    return null
  }
  const shortId = slug.slice(lastHyphen + 1)
  // Short IDs are 7-8 characters of base64url
  if (shortId.length >= 7 && shortId.length <= 8) {
    return shortId
  }
  return null
}

/**
 * Create an entity reference slug from name and short ID
 * @example
 * createEntityRef('ABC Aviation', 'VQ6EAOK') // 'abc-aviation-VQ6EAOK'
 */
export function createEntityRef(name: string, shortId: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50)

  return `${slug}-${shortId}`
}
